import React, { useState, useEffect } from 'react';
import createAxiosInstance from '../../utils/axiosConfig';
import Alert from './alerts/alert';
import { create_scoring_option } from '../../utils/endpoints';

interface ScoringOptionsEditSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  frameworkId: number;
}

interface ScoringOptionFormData {
  framework_id: number;
  option_name: string;
  score: number;
  tooltip_content: { [key: string]: string[] };
  is_enabled: boolean;
  is_deleted: boolean;
  id?: number;
}

const ScoringOptionsEditSidebar: React.FC<ScoringOptionsEditSidebarProps> = ({
  isOpen,
  onClose,
  frameworkId
}) => {
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
  const [scoringOptions, setScoringOptions] = useState<any[]>([]);
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [formData, setFormData] = useState<ScoringOptionFormData>({
    framework_id: frameworkId,
    option_name: '',
    score: 0,
    tooltip_content: {},
    is_enabled: true,
    is_deleted: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [tooltipKeys, setTooltipKeys] = useState<{ key: string; values: string[] }[]>([{ key: '', values: [''] }]);
  // Add new state to track whether we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  // Reset form when selected option changes
  useEffect(() => {
    if (selectedOption) {
      // Convert tooltip_content object to tooltipKeys array format
      const tooltipKeysArray = Object.entries(selectedOption.tooltip_content).map(([key, values]) => ({
        key,
        values: Array.isArray(values) ? values : [values]
      }));

      setFormData({
        framework_id: frameworkId,
        option_name: selectedOption.option_name,
        score: selectedOption.score,
        tooltip_content: selectedOption.tooltip_content,
        is_enabled: selectedOption.is_enabled,
        is_deleted: selectedOption.is_deleted,
        id: selectedOption.id
      });
      setTooltipKeys(tooltipKeysArray);
    }
  }, [selectedOption, frameworkId]);

  // Fetch scoring options when sidebar opens
  useEffect(() => {
    if (isOpen && frameworkId) {
      fetchScoringOptions();
      setSelectedOption(null); // Reset selected option when opening sidebar
      setFormData({
        framework_id: frameworkId,
        option_name: '',
        score: 0,
        tooltip_content: {},
        is_enabled: true,
        is_deleted: false
      });
      setTooltipKeys([{ key: '', values: [''] }]);
      setIsEditMode(false); // Reset to add mode when opening
    }
  }, [isOpen, frameworkId]);

  const fetchScoringOptions = async () => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`/maturity_frameworks/framework_options/framework/${frameworkId}`);
      
      if (response.status === 200) {
        const options = response.data.data || response.data;
        setScoringOptions(options);
        
        // Only set selected option if we're in edit mode
        if (isEditMode && options.length > 0) {
          setSelectedOption(options[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching scoring options:', error);
      setAlert({
        message: 'Error fetching scoring options. Please try again.',
        type: 'danger',
        show: true
      });
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.option_name.trim()) {
      newErrors.option_name = 'Option name is required';
    }
    
    if (formData.score < 0) {
      newErrors.score = 'Score must be a non-negative number';
    }
    
    // Validate tooltip keys and values
    const hasEmptyKey = tooltipKeys.some(item => !item.key.trim());
    const hasEmptyValue = tooltipKeys.some(item => item.values.some(value => !value.trim()));
    
    if (hasEmptyKey) {
      newErrors.tooltip_key = 'All tooltip keys must be filled';
    }
    
    if (hasEmptyValue) {
      newErrors.tooltip_value = 'All tooltip values must be filled';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'score' ? parseInt(value) || 0 : value)
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTooltipChange = (keyIndex: number, field: 'key' | 'value', value: string, valueIndex?: number) => {
    setTooltipKeys(prev => {
      const updated = [...prev];
      if (field === 'key') {
        updated[keyIndex].key = value;
      } else if (valueIndex !== undefined) {
        updated[keyIndex].values[valueIndex] = value;
      }
      return updated;
    });
  };

  const addTooltipKey = () => {
    setTooltipKeys(prev => [...prev, { key: '', values: [''] }]);
  };

  const removeTooltipKey = (index: number) => {
    setTooltipKeys(prev => prev.filter((_, i) => i !== index));
  };

  const addTooltipValue = (keyIndex: number) => {
    setTooltipKeys(prev => {
      const updated = [...prev];
      updated[keyIndex].values.push('');
      return updated;
    });
  };

  const removeTooltipValue = (keyIndex: number, valueIndex: number) => {
    setTooltipKeys(prev => {
      const updated = [...prev];
      updated[keyIndex].values = updated[keyIndex].values.filter((_, i) => i !== valueIndex);
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();
      
      // Convert tooltipKeys to tooltip_content format
      const tooltip_content = tooltipKeys.reduce((acc, { key, values }) => {
        if (key.trim()) {
          acc[key.trim()] = values.filter(v => v.trim());
        }
        return acc;
      }, {} as { [key: string]: string[] });

      const payload = {
        ...formData,
        tooltip_content
      };

      let response;
      if (isEditMode && selectedOption) {
        // Update existing option
        response = await axiosInstance.put(`/maturity_frameworks/framework_options/${formData.id}`, payload);
      } else {
        // Create new option
        response = await axiosInstance.post(create_scoring_option, payload);
      }

      if (response.status === 200 || response.status === 201) {
        setAlert({
          message: `Scoring option ${isEditMode ? 'updated' : 'created'} successfully!`,
          type: 'success',
          show: true
        });
        
        // Refresh the scoring options list
        await fetchScoringOptions();
        
        // Reset form only if we're in add mode
        if (!isEditMode) {
          setFormData({
            framework_id: frameworkId,
            option_name: '',
            score: 0,
            tooltip_content: {},
            is_enabled: true,
            is_deleted: false
          });
          setTooltipKeys([{ key: '', values: [''] }]);
        }
      }
    } catch (error) {
      console.error('Error saving scoring option:', error);
      setAlert({
        message: `Error ${isEditMode ? 'updating' : 'creating'} scoring option. Please try again.`,
        type: 'danger',
        show: true
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      framework_id: frameworkId,
      option_name: '',
      score: 0,
      tooltip_content: {},
      is_enabled: true,
      is_deleted: false
    });
    setTooltipKeys([{ key: '', values: [''] }]);
    setErrors({});
    setAlert(null);
    onClose();
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (!isEditMode && scoringOptions.length > 0) {
      setSelectedOption(scoringOptions[0]);
    } else {
      setSelectedOption(null);
      setFormData({
        framework_id: frameworkId,
        option_name: '',
        score: 0,
        tooltip_content: {},
        is_enabled: true,
        is_deleted: false
      });
      setTooltipKeys([{ key: '', values: [''] }]);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="offcanvas-backdrop fade show"
        onClick={handleClose}
      ></div>
      <div
        className="offcanvas offcanvas-end show"
        tabIndex={-1}
        style={{
          zIndex: 1050,
          width: '40vw',
          maxWidth: '500px'
        }}
      >
        <div className="offcanvas-header" style={{backgroundColor: 'white', borderBottom: '1px solid #dee2e6'}}>
          <h5 className="offcanvas-title" style={{
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#183FCE'
          }}>
            {isEditMode ? 'Edit Scoring Options' : 'Add Scoring Options'}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={handleClose}
          ></button>
        </div>

        <div className="offcanvas-body">
          {scoringOptions.length > 0 && (
            <div className="mb-4 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-link p-0"
                onClick={toggleEditMode}
                style={{ 
                  color: '#183FCE', 
                  textDecoration: 'none',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                {isEditMode ? 'Add New Option' : 'Edit Existing Options'}
              </button>
            </div>
          )}

          {isEditMode ? (
            // Show dropdown and edit form for existing options
            <>
              <div className="mb-4">
                <label htmlFor="scoringOptionSelect" className="form-label">Select Scoring Option:</label>
                <select
                  id="scoringOptionSelect"
                  className="form-select bg-white"
                  value={selectedOption?.id || (scoringOptions[0]?.id || '')}
                  onChange={(e) => {
                    const selected = scoringOptions.find(option => option.id === parseInt(e.target.value));
                    if (selected) {
                      setSelectedOption(selected);
                    }
                  }}
                  disabled={loading}
                >
                  {scoringOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.option_name} (Score: {option.score})
                    </option>
                  ))}
                </select>
              </div>

              {selectedOption && (
                <div className="border-top pt-4">
                  {/* Form fields for editing */}
                  <div className="mb-3">
                    <label htmlFor="optionName" className="form-label">Option Name:</label>
                    <input
                      type="text"
                      className={`form-control bg-white ${errors.option_name ? 'is-invalid' : ''}`}
                      id="optionName"
                      name="option_name"
                      value={formData.option_name}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    {errors.option_name && (
                      <div className="invalid-feedback">{errors.option_name}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label htmlFor="score" className="form-label">Score:</label>
                    <input
                      type="number"
                      className={`form-control bg-white ${errors.score ? 'is-invalid' : ''}`}
                      id="score"
                      name="score"
                      min="0"
                      step="1"
                      value={formData.score}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    {errors.score && (
                      <div className="invalid-feedback">{errors.score}</div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Tooltip Content:</label>
                    {tooltipKeys.map((item, keyIndex) => (
                      <div key={keyIndex} className="mb-3 border rounded p-3">
                        <div className="mb-2">
                          <label className="form-label small">Key:</label>
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              className={`form-control bg-white ${errors.tooltip_key ? 'is-invalid' : ''}`}
                              value={item.key}
                              onChange={(e) => handleTooltipChange(keyIndex, 'key', e.target.value)}
                              disabled={loading}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() => removeTooltipKey(keyIndex)}
                              disabled={loading || tooltipKeys.length === 1}
                            >
                              -
                            </button>
                          </div>
                        </div>

                        <div className="ms-3">
                          <label className="form-label small">Values:</label>
                          {item.values.map((value, valueIndex) => (
                            <div key={valueIndex} className="d-flex gap-2 mb-2">
                              <input
                                type="text"
                                className={`form-control bg-white ${errors.tooltip_value ? 'is-invalid' : ''}`}
                                value={value}
                                onChange={(e) => handleTooltipChange(keyIndex, 'value', e.target.value, valueIndex)}
                                disabled={loading}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => removeTooltipValue(keyIndex, valueIndex)}
                                disabled={loading || item.values.length === 1}
                              >
                                -
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => addTooltipValue(keyIndex)}
                            disabled={loading}
                          >
                            + Add Value
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={addTooltipKey}
                      disabled={loading}
                    >
                      + Add Tooltip Key
                    </button>
                  </div>

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="optionEnabled"
                        name="is_enabled"
                        checked={formData.is_enabled}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="form-check-label" htmlFor="optionEnabled">
                        Enable Option
                      </label>
                    </div>
                  </div>

                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="optionDeleted"
                        name="is_deleted"
                        checked={formData.is_deleted}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="form-check-label" htmlFor="optionDeleted">
                        Mark as Deleted
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Show create form when no options exist
            <div>
              <div className="mb-3">
                <label htmlFor="optionName" className="form-label">Option Name:</label>
                <input
                  type="text"
                  className={`form-control bg-white ${errors.option_name ? 'is-invalid' : ''}`}
                  id="optionName"
                  name="option_name"
                  value={formData.option_name}
                  onChange={handleChange}
                  disabled={loading}
                />
                {errors.option_name && (
                  <div className="invalid-feedback">{errors.option_name}</div>
                )}
              </div>

              <div className="mb-3">
                <label htmlFor="score" className="form-label">Score:</label>
                <input
                  type="number"
                  className={`form-control bg-white ${errors.score ? 'is-invalid' : ''}`}
                  id="score"
                  name="score"
                  min="0"
                  step="1"
                  value={formData.score}
                  onChange={handleChange}
                  disabled={loading}
                />
                {errors.score && (
                  <div className="invalid-feedback">{errors.score}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Tooltip Content:</label>
                {tooltipKeys.map((item, keyIndex) => (
                  <div key={keyIndex} className="mb-3 border rounded p-3">
                    <div className="mb-2">
                      <label className="form-label small">Key:</label>
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          className={`form-control bg-white ${errors.tooltip_key ? 'is-invalid' : ''}`}
                          value={item.key}
                          onChange={(e) => handleTooltipChange(keyIndex, 'key', e.target.value)}
                          disabled={loading}
                        />
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeTooltipKey(keyIndex)}
                          disabled={loading || tooltipKeys.length === 1}
                        >
                          -
                        </button>
                      </div>
                    </div>

                    <div className="ms-3">
                      <label className="form-label small">Values:</label>
                      {item.values.map((value, valueIndex) => (
                        <div key={valueIndex} className="d-flex gap-2 mb-2">
                          <input
                            type="text"
                            className={`form-control bg-white ${errors.tooltip_value ? 'is-invalid' : ''}`}
                            value={value}
                            onChange={(e) => handleTooltipChange(keyIndex, 'value', e.target.value, valueIndex)}
                            disabled={loading}
                          />
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => removeTooltipValue(keyIndex, valueIndex)}
                            disabled={loading || item.values.length === 1}
                          >
                            -
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => addTooltipValue(keyIndex)}
                        disabled={loading}
                      >
                        + Add Value
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={addTooltipKey}
                  disabled={loading}
                >
                  + Add Tooltip Key
                </button>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="optionEnabled"
                    name="is_enabled"
                    checked={formData.is_enabled}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <label className="form-check-label" htmlFor="optionEnabled">
                    Enable Option
                  </label>
                </div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="optionDeleted"
                    name="is_deleted"
                    checked={formData.is_deleted}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <label className="form-check-label" htmlFor="optionDeleted">
                    Mark as Deleted
                  </label>
                </div>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-light"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-rounded"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                isEditMode ? 'Update Scoring Option' : 'Create Scoring Option'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert && (
        <Alert
          message={alert.message}
          type={alert.type}
          show={alert.show}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default ScoringOptionsEditSidebar; 